import { useEffect, useState } from 'react'
import { configApi } from '@/services/api/config'
import type { PermissionState } from '@/services/api/config'

interface FieldDelegationControlProps {
  configType: string
  fieldKey: string
  fieldType: string
  registryMin: number | null
  registryMax: number | null
  perm: PermissionState | undefined
  /** Called with the merged permissions returned by the PUT (no extra GET needed). */
  onSaved: (merged: Record<string, PermissionState>) => void
  onError: (msg: string) => void
  /** hr2-v2 (E6): gepufferter Modus — Klick puffert nur (kein API-Call);
   * der übergeordnete Dirty-Save schreibt den Zustand mit. */
  mode?: 'immediate' | 'buffered'
  /** hr2-v2 (E6): Rollen-Label für Tooltips (z.B. 'HR' statt 'Lehrer'). */
  roleLabel?: string
  /** Nur im gepufferten Modus: aktueller (ggf. entwarfener) Zustand als
   * sichtbar/änderbar-Paar, falls kein PermissionState-Objekt vorliegt. */
  bufferedPerm?: { visible: boolean; editable: boolean } | undefined
  onBufferedChange?: (next: { visible: boolean; editable: boolean }) => void
}

type State = 'versteckt' | 'sichtbar' | 'aenderbar'

/**
 * Per-field delegation control (Versteckt / Sichtbar / Änderbar),
 * rendered inline on every delegatable setting row of a config card.
 *
 * Immediate mode (Lehrer): sends ONLY this key's permission via PUT; min/max
 * are always included so the stored range is never wiped (server: omitted
 * keys keep values, explicit null clears).
 *
 * Buffered mode (hr2-v2, Ziel-Rolle HR): clicks update a draft that travels
 * with the page's single dirty save (E2/E15).
 */
export function FieldDelegationControl({
  configType,
  fieldKey,
  fieldType,
  registryMin,
  registryMax,
  perm,
  onSaved,
  onError,
  mode = 'immediate',
  roleLabel = 'Lehrer',
  bufferedPerm,
  onBufferedChange,
}: FieldDelegationControlProps) {
  const [saving, setSaving] = useState(false)
  const [minDraft, setMinDraft] = useState<string>(perm?.min_value != null ? String(perm.min_value) : '')
  const [maxDraft, setMaxDraft] = useState<string>(perm?.max_value != null ? String(perm.max_value) : '')

  useEffect(() => {
    setMinDraft(perm?.min_value != null ? String(perm.min_value) : '')
    setMaxDraft(perm?.max_value != null ? String(perm.max_value) : '')
  }, [perm?.min_value, perm?.max_value])

  // hr2-v2: im gepufferten Modus beschreibt bufferedPerm den Zustand
  const effectivePerm: PermissionState | undefined = mode === 'buffered' && bufferedPerm
    ? { visible: bufferedPerm.visible, editable: bufferedPerm.editable, min_value: null, max_value: null }
    : perm

  const state: State = !effectivePerm?.visible ? 'versteckt' : effectivePerm.editable ? 'aenderbar' : 'sichtbar'

  const save = async (target: State, minMax?: { min_value: number | null; max_value: number | null }) => {
    // hr2-v2 (E6): gepuffert — kein API-Call, nur Draft-Update
    if (mode === 'buffered') {
      onBufferedChange?.({
        visible: target !== 'versteckt',
        editable: target === 'aenderbar',
      })
      return
    }
    setSaving(true)
    try {
      // Only send min/max when explicitly managing the range OR when a row
      // already exists (send its stored values back). Never send nulls for a
      // field without known state — the backend would interpret explicit
      // null as "clear", and omitting entirely means "keep stored".
      const entry: { visible: boolean; editable: boolean; min_value?: number | null; max_value?: number | null } = {
        visible: target !== 'versteckt',
        editable: target === 'aenderbar',
      }
      if (minMax) {
        entry.min_value = minMax.min_value
        entry.max_value = minMax.max_value
      } else if (perm) {
        entry.min_value = perm.min_value
        entry.max_value = perm.max_value
      }
      const res = await configApi.updatePermissions(configType, { [fieldKey]: entry })
      onSaved(res.permissions)
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Delegation fehlgeschlagen')
    } finally {
      setSaving(false)
    }
  }

  const applyState = (target: State) => {
    if (target === state) return
    save(target) // keep existing range when toggling state
  }

  const saveRange = () => {
    const minVal = minDraft === '' ? null : Number(minDraft)
    const maxVal = maxDraft === '' ? null : Number(maxDraft)
    if (minVal === (perm?.min_value ?? null) && maxVal === (perm?.max_value ?? null)) return
    save('aenderbar', { min_value: minVal, max_value: maxVal })
  }

  const btn = (id: State, icon: React.ReactNode, label: string) => (
    <button
      key={id}
      onClick={() => applyState(id)}
      disabled={saving}
      title={`${roleLabel}: ${label}`}
      className={`p-1.5 rounded-md transition text-sm leading-none ${
        state === id
          ? 'bg-white shadow-sm font-semibold text-gray-800'
          : 'text-gray-400 hover:text-gray-600'
      } ${saving ? 'opacity-50 cursor-wait' : ''}`}
    >
      <span className="sr-only">{label}</span>
      {icon}
    </button>
  )

  return (
    <div className="flex-shrink-0 flex flex-col items-end gap-1 border-l border-gray-200 pl-3 ml-1">
      <div className="flex items-center gap-0.5 p-0.5 bg-gray-100 rounded-lg">
        {btn('versteckt', <span aria-hidden>🔒</span>, 'Versteckt')}
        {btn('sichtbar', <span aria-hidden>👁</span>, 'Sichtbar')}
        {btn('aenderbar', <span aria-hidden>✏️</span>, 'Änderbar')}
      </div>
      {mode === 'immediate' && fieldType === 'int' && state === 'aenderbar' && (
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-gray-400">Bereich</span>
          <input
            type="number"
            value={minDraft}
            onChange={e => setMinDraft(e.target.value)}
            onBlur={saveRange}
            placeholder={registryMin != null ? String(registryMin) : 'min'}
            className="w-14 px-1.5 py-0.5 text-[11px] border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
          />
          <span className="text-[10px] text-gray-400">–</span>
          <input
            type="number"
            value={maxDraft}
            onChange={e => setMaxDraft(e.target.value)}
            onBlur={saveRange}
            placeholder={registryMax != null ? String(registryMax) : 'max'}
            className="w-14 px-1.5 py-0.5 text-[11px] border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      )}
    </div>
  )
}
